/**
 * Qui fait lire la carte de mutuelle, et quand.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE QUI A CHANGE LE 01/10
 * ─────────────────────────────────────────────────────────────────────────
 *
 * La tablette lisait la carte au moment de la photographier. Elle ne le fait
 * plus : c'est le back-office qui la fait lire par le serveur, quand le
 * secretariat ouvre le dossier. Deux raisons, dans cet ordre :
 *
 *   — la secrétaire est DEVANT la carte ouverte : elle voit tout de suite si
 *     la lecture a marché, et peut relancer ;
 *   — la lecture ne se paie plus sur le temps de passage du preleveur.
 *
 * Le serveur ecrit le resultat sur le rendez-vous (`mutuelleResume`), donc
 * l'ecoute Firestore deja en place l'affiche sans qu'on touche a l'ecran.
 *
 * La decision « faut-il lancer la lecture ? » est ici, seule, sans React :
 * c'est la seule partie qui merite un test, et l'ecran n'a plus qu'a
 * l'appeler.
 */

/**
 * Le verrou du serveur, en millisecondes.
 *
 * C'est le meme chiffre que `VERROU_MS` de `card_ocr_controller` : au-dela, un
 * `enCours` est un appel mort (fonction coupee, redeploiement) et ne bloque
 * plus personne. Les deux valeurs doivent rester d'accord — sinon on remet en
 * file une lecture que le serveur croit encore en cours, ou l'inverse.
 */
export const VERROU_MS = 60000;

/**
 * Un horodatage Firestore (Timestamp, `{seconds}` ou `{_seconds}`) en
 * millisecondes. `null` quand il n'y en a pas : un instantane rend parfois
 * `null` a la place d'un `serverTimestamp()` pas encore pose.
 */
export function enMillisecondes(valeur) {
  if (!valeur) return null;
  const secondes = valeur.seconds ?? valeur._seconds;
  if (typeof secondes === "number") return secondes * 1000;
  if (typeof valeur === "number") return valeur;
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
}

/**
 * Faut-il demander au serveur de lire la carte de ce rendez-vous ?
 *
 * Cinq raisons de ne PAS le faire, et chacune a coute quelque chose :
 *
 *   — pas de photo de mutuelle : il n'y a rien a lire ;
 *   — `mutuelleResume` deja la : le serveur a repondu, on ne redemande pas ;
 *   — `mutuelleLue.echec` : il a DEJA repondu qu'il n'y arrivait pas. Sans
 *     cette garde, chaque ouverture du dossier relancait une lecture vouee a
 *     l'echec, et la zone restait sur « Lecture… » pour rien ;
 *   — `mutuelleLue.enCours` RECENT : un autre poste (ou la tablette) tient le
 *     verrou du serveur. Depuis le 01/10 au soir la lecture part toute seule
 *     des la liste chargee : sans cette garde, deux postes ouverts sur le meme
 *     dossier lanceraient deux lectures, et le serveur refuserait la seconde
 *     en la comptant comme un appel de plus ;
 *   — l'identifiant est deja dans `dejaLancees` (un Set, une fois par dossier
 *     et par session) : la liste des dossiers se rafraichit toutes les 30 s,
 *     l'effet se relance a chaque instantane, et sans cette garde on paierait
 *     une lecture par rafraichissement.
 *
 * `maintenant` est injectable pour les tests : le reste du temps c'est
 * l'horloge du poste, comme le serveur fait avec la sienne.
 *
 * Le bouton « Relire » ne passe pas par ici : il appelle le serveur avec
 * `force: true`, c'est-a-dire en le dispensant de ses propres gardes.
 */
export function fautLancerLecture(rendezVous, dejaLancees, { maintenant = Date.now() } = {}) {
  if (!rendezVous?.id) return false;
  if (!rendezVous.mutual) return false;
  if (rendezVous.mutuelleResume) return false;
  if (rendezVous.mutuelleLue?.echec) return false;
  const enCours = enMillisecondes(rendezVous.mutuelleLue?.enCours);
  if (enCours && maintenant - enCours < VERROU_MS) return false;
  if (dejaLancees?.has?.(rendezVous.id)) return false;
  return true;
}

/**
 * L'horodatage qui date un dossier, du plus parlant au moins parlant.
 *
 * `ended_at` est celui sur lequel la liste est deja triee (la requete de
 * l'ecran) ; les suivants servent aux dossiers d'avant, ou a un Mode direct
 * qui n'a pas encore d'heure de fin. `priseEnChargeDebut` vient en dernier
 * parce qu'il est le plus recent dans l'histoire du produit.
 */
function dateDossier(rendezVous) {
  const brut =
    rendezVous?.ended_at ||
    rendezVous?.started_at ||
    rendezVous?.created_at ||
    rendezVous?.priseEnChargeDebut;
  return enMillisecondes(brut) ?? 0;
}

/**
 * Quels dossiers mettre en file de lecture, et dans quel ordre.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * POURQUOI UNE FILE, ET PAS UNE LECTURE PAR DOSSIER QUI PART TOUTE SEULE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le client, le 01/10 au soir : « il faut que la lecture soit deja faite
 * quand j'ouvre le dossier ». On ne peut donc plus attendre qu'un dossier soit
 * ouvert : on met en file tous ceux de la liste chargee qui portent une photo
 * de mutuelle jamais lue.
 *
 * Mais lancer trente lectures d'un coup est pire que le mal : trente appels
 * payants au modele a la seconde, et une page qui ne repond plus. La file les
 * prend UN PAR UN, et l'ecran ne l'attend jamais — c'est l'affichage qui
 * commande, la file suit derriere.
 *
 * L'ORDRE : le plus recent d'abord. C'est celui que la secretaire va ouvrir —
 * la liste est triee comme ca, et on travaille la journee de haut en bas.
 *
 * `dejaLancees` est le Set que l'ecran garde par session : un dossier deja
 * lance n'y est jamais remis, meme si un instantane le represente.
 */
export function dossiersAMettreEnFile(rendezVous, dejaLancees, options = {}) {
  const liste = Array.isArray(rendezVous) ? rendezVous : [];
  // Seulement les dossiers des dernières 24 h : la liste charge plusieurs
  // jours, et relire tout l'arriéré coûterait un appel au modèle par dossier
  // pour des passages déjà traités. Un dossier plus ancien se lit encore à
  // son ouverture.
  const maintenant = options.maintenant ?? Date.now();
  const ageMax = options.ageMaxMs ?? 24 * 60 * 60 * 1000;
  return liste
    .filter((rdv) => fautLancerLecture(rdv, dejaLancees, options))
    .filter((rdv) => {
      const d = dateDossier(rdv);
      // Sans aucune heure, on ne sait pas : on garde (cas rare).
      return d === 0 || maintenant - d <= ageMax;
    })
    .sort((a, b) => dateDossier(b) - dateDossier(a));
}

/**
 * Ce que la reponse du serveur dit de l'etat de la lecture.
 *
 * Le contrat serveur est : `{ success, mutuelleResume | null, raison?,
 * enCours?, deja? }`. On ne devine rien : `enCours` d'abord (la tablette a
 * peut-etre deja lance la lecture de son cote), puis le resume, puis l'echec.
 */
export function etatDepuisReponse(reponse) {
  if (!reponse) return "illisible";
  if (reponse.enCours) return "en_cours";
  if (reponse.success && reponse.mutuelleResume) return "lue";
  return "illisible";
}